import React, { Component } from 'react';
import { RouteComponentProps } from 'src/hoc/withRouter';
import { EventBus } from 'src/helpers/new';
import { RootState } from 'src/store/reducers/rootReducer';
import CheckoutContext from '../../../CheckoutContext';
import Next from '../../Next';
import { CheckoutValidateHelper } from '../CheckoutValidateHelper';
import withRouterAndRedux from 'src/hoc/withRouterAndRedux';
import * as Sentry from '@sentry/react';
import validator from 'validator';
import { getState } from 'src/helpers/localStorage';
import { uniqBy } from 'lodash';
import CheckoutHelper from 'src/pages/Checkout/CheckoutHelper';
import PhoneInput from 'src/components/inputs/PhoneInput';

type TProps = RouteComponentProps & IConnectProps;

interface Course {
    purchaseType: string;
}

interface IConnectProps {
    loggedIn: any;
}

export interface IContact {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    phoneNumber: string;
    startedAt: string;
    updatedAt: string;
    createdAt: string;
}

interface IState {
    contact: IContact;
    validation: {
        firstNameMessage: string;
        lastNameMessage: string;
        emailMessage: string;
        confirmEmailMessage: string;
        phoneNumberMessage: string;
    };
}

class Contact extends Component<TProps, IState> {
    static readonly contextType = CheckoutContext;
    context!: React.ContextType<typeof CheckoutContext>;

    constructor(props: TProps, context: any) {
        super(props);
        this.state = {
            contact: {
                ...context.contact,
                startedAt: context.contact.startedAt || new Date(),
                phoneNumber: context.contact.phoneNumber || context.user.phoneNumber || '',
            },
            validation: {
                firstNameMessage: '',
                lastNameMessage: '',
                emailMessage: '',
                confirmEmailMessage: '',
                phoneNumberMessage: '',
            },
        };
    }

    componentDidMount() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
        if (this.context.tabs?.[1]?.completed) {
            this.setState((prevState) => ({
                contact: { ...prevState.contact, confirmEmail: this.context.contact.email },
            }));
        }
        EventBus.on('validate-contact-tab', this.validateInputs);
    }

    componentWillUnmount() {
        EventBus.remove('validate-contact-tab', this.validateInputs);
    }

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState((prevState) => {
            const contact = { ...prevState.contact };

            if (e.target.name === 'email') {
                contact[e.target.name as keyof IContact] = e.target.value.toLowerCase();
            } else if (e.target.name === 'phoneNumber') {
                const phoneValue = e.target.value.replace(/\D/g, '');
                contact[e.target.name as keyof IContact] = phoneValue;
            } else {
                contact[e.target.name as keyof IContact] = e.target.value;
            }

            this.context.updateCheckoutState({
                contact: contact,
            });

            return { contact };
        });
    };

    handlePhoneInputChange = (value: string) => {
        this.setState((prevState) => {
            const contact = { ...prevState.contact, phoneNumber: value };
            const validation = { ...prevState.validation, phoneNumberMessage: '' };

            if (this.context.updateCheckoutState) {
                this.context.updateCheckoutState({ contact });
            }

            return { contact, validation };
        });
    };

    hanldeCopyPasteConfiramtionEmail = (e: any) => {
        e.preventDefault();
    };

    validateInputs = async () => {
        try {
            const { proceed, messages } = CheckoutValidateHelper.validateContact(
                this.state.validation,
                this.state.contact,
            );
            const { isAdminPreview } = this.context;
            const userFromLocalStorage = localStorage.getItem('user');
            let userData, hasProctorOrChat;
            if (userFromLocalStorage) {
                userData = JSON.parse(userFromLocalStorage ?? '');
                const courses = this.context.packages.filter((pkg: any) => pkg.hasOwnProperty('purchaseType'));

                hasProctorOrChat = courses.some(
                    (course: Course) =>
                        course.purchaseType === 'proctorPass' ||
                        course.purchaseType === 'chat' ||
                        course.purchaseType === 'extension' ||
                        course.purchaseType === 'repurchase',
                );
            }

            if (proceed || isAdminPreview) {
                if (hasProctorOrChat && userData?.email !== this.state.contact.email && !isAdminPreview) {
                    messages.emailMessage = "You can't change the email for proctor/chat/extension/repurchase!";
                    messages.confirmEmailMessage = '';
                    this.setState({ validation: messages });
                } else {
                    this.context.switchTab(2, true);
                    await this.context.updateCart({
                        contact: {
                            ...this.state.contact,
                            firstName: this.state.contact?.firstName.trim(),
                            lastName: this.state.contact?.lastName.trim(),
                            createdAt: this.context.contact.createdAt ?? new Date(),
                            updatedAt: new Date(),
                        },
                        currentTab: 2,
                        tcpaAccepted: new Date(),
                    });
                    if (!isAdminPreview) {
                        CheckoutHelper.metaTrackEvent('Contact');
                    }
                }
            } else {
                this.setState({
                    validation: messages,
                });
            }

            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    redirectLogin = () => {
        localStorage.removeItem('authToken');

        if (!this.context.isAdminPreview) {
            const packages = this.context.packages
                .filter((v: any) => !v.isBundle)
                .map((packageInfo: any) => ({
                    packageId: packageInfo.packageId,
                    packagePurchaseType: packageInfo.packagePurchaseType,
                    source: packageInfo.source || '',
                    campaign: packageInfo.campaign || '',
                    medium: packageInfo.medium || '',
                    timestamp: packageInfo.timestamp || '',
                    isUpsell: packageInfo.isUpsell,
                    upsellLocation: packageInfo.upsellLocation || '',
                }));
            const bundles = this.context.packages
                .filter((v: any) => v.isBundle)
                .map((packageInfo: any) => ({
                    bundleId: packageInfo.bundleId,
                    packagePurchaseType: packageInfo.packagePurchaseType,
                    source: packageInfo.source || '',
                    campaign: packageInfo.campaign || '',
                    medium: packageInfo.medium || '',
                    timestamp: packageInfo.timestamp || '',
                }));
            const uniqueBundles = uniqBy(bundles, 'bundleId');

            const data = {
                packages,
                bundles: uniqueBundles,
                cartId: getState('reuCheckoutCartId'),
            };

            sessionStorage.setItem('login-checkout-data', JSON.stringify(data));
        }

        window.location.href = '/login';
    };

    checkedEmail = async () => {
        const { email } = this.state.contact;

        if (email && validator.isEmail(email)) {
            const { inUse, canUse } = await CheckoutValidateHelper.checkEmail(
                email,
                this.props.loggedIn?.user?._id ?? '',
            );
            this.setState((prevState) => {
                const messages = { ...prevState.validation };
                if (inUse && !canUse) {
                    messages.emailMessage = 'Email Already Registered';
                } else {
                    messages.emailMessage = '';
                }
                return { validation: messages };
            });
        }
    };

    render() {
        const { firstName, lastName, email, confirmEmail, phoneNumber } = this.state.contact;
        const { firstNameMessage, lastNameMessage, emailMessage, confirmEmailMessage, phoneNumberMessage } =
            this.state.validation;

        return (
            <>
                <div className='checkout-contact'>
                    <h1>Contact info</h1>
                    <h3>{`Please write your name exactly as it appears on your driver's license or ID.`}</h3>
                    <div className='checkout-form'>
                        <div>
                            <label htmlFor='cFirstName'>First name *</label>
                            <input
                                type='text'
                                value={firstName}
                                onChange={this.handleInputChange}
                                name='firstName'
                                id='cFirstName'
                            />
                            <p>{firstNameMessage}</p>
                        </div>
                        <div>
                            <label htmlFor='cLastName'>Last name *</label>
                            <input
                                type='text'
                                value={lastName}
                                onChange={this.handleInputChange}
                                name='lastName'
                                id='cLastName'
                            />
                            <p>{lastNameMessage}</p>
                        </div>
                        <div>
                            <label htmlFor='cEmail'>Email *</label>
                            <input
                                type='email'
                                value={email}
                                onChange={this.handleInputChange}
                                name='email'
                                id='cEmail'
                                onBlur={this.checkedEmail}
                            />
                            <p>{emailMessage}</p>
                            {emailMessage === 'Email Already Registered' ? (
                                <button onClick={this.redirectLogin} className='login-link'>
                                    Login to your account
                                </button>
                            ) : (
                                ''
                            )}
                        </div>

                        <div>
                            <label htmlFor='cConfirmEmail'>Email address confirmation *</label>
                            <input
                                type='email'
                                value={confirmEmail}
                                onChange={this.handleInputChange}
                                onPaste={this.hanldeCopyPasteConfiramtionEmail}
                                name='confirmEmail'
                                id='cConfirmEmail'
                            />
                            <p>{confirmEmailMessage}</p>
                        </div>

                        <div className='mb-4'>
                            <label htmlFor='cPhoneNumber'>Mobile number *</label>
                            <PhoneInput id='cPhoneNumber' value={phoneNumber} onChange={this.handlePhoneInputChange} />
                            {phoneNumberMessage && <p>{phoneNumberMessage}</p>}
                        </div>
                    </div>
                </div>
                <Next onClick={this.validateInputs} />
            </>
        );
    }
}

export default withRouterAndRedux(
    Contact,
    (state: RootState) => {
        return {
            loggedIn: state.loggedIn,
        };
    },
    null,
);
